import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star } from "lucide-react";
import { cn } from "@/lib/utils";

export type VisaPathway = {
  id: string;
  title: string;
  description: string;
  rating: number;
  processingTime: string;
  requirements: {
    label: string;
    value: string;
  }[];
};

const visaPathways: VisaPathway[] = [
  {
    id: "global-talent",
    title: "Global Talent Visa (UK)",
    description: "For exceptional talent in science, engineering, humanities, and digital technology",
    rating: 4.5,
    processingTime: "3-8 weeks",
    requirements: [
      { label: "Education", value: "PhD or equivalent experience" },
      { label: "Experience", value: "5+ years in field" },
      { label: "Achievements", value: "Significant contributions" }
    ]
  },
  {
    id: "eb1",
    title: "EB-1A Visa (USA)",
    description: "For individuals with extraordinary ability in sciences, arts, education, business, or athletics",
    rating: 4.3,
    processingTime: "6-8 months",
    requirements: [
      { label: "Achievement", value: "International recognition" },
      { label: "Evidence", value: "Awards/publications" },
      { label: "Impact", value: "Field leadership" }
    ]
  },
  {
    id: "dubai-golden",
    title: "Dubai Golden Visa",
    description: "For investors, entrepreneurs, specialized talents, and outstanding graduates",
    rating: 4.4,
    processingTime: "2-4 weeks",
    requirements: [
      { label: "Investment", value: "AED 2M+ or specialized talent" },
      { label: "Experience", value: "Proven expertise" },
      { label: "Recognition", value: "Professional achievements" }
    ]
  },
  {
    id: "high-potential",
    title: "High Potential Individual",
    description: "For recent graduates from top global universities",
    rating: 4.2,
    processingTime: "2-3 weeks",
    requirements: [
      { label: "Education", value: "Top university graduate" },
      { label: "Graduation", value: "Last 5 years" },
      { label: "Degree Level", value: "Bachelor's or higher" }
    ]
  }
];

interface VisaPathwaySelectionProps {
  onSelect: (pathway: VisaPathway) => void;
}

const VisaPathwaySelection = ({ onSelect }: VisaPathwaySelectionProps) => {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Choose Your Visa Pathway</h2>
        <p className="text-muted-foreground">
          Select the visa category that best matches your profile and aspirations
        </p>
      </div>

      <div className="grid gap-6">
        {visaPathways.map((pathway) => (
          <Card
            key={pathway.id}
            className={cn(
              "relative overflow-hidden transition-all duration-300",
              "hover:shadow-lg hover:border-primary/50 cursor-pointer",
              "bg-card/50 backdrop-blur-sm"
            )}
            onClick={() => onSelect(pathway)}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold">{pathway.title}</h3>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm">{pathway.rating}</span>
                </div>
              </div>

              <p className="text-muted-foreground mb-4">{pathway.description}</p>

              <div className="space-y-3">
                <div className="text-sm">
                  <span className="font-medium">Processing time:</span>{" "}
                  <span className="text-muted-foreground">{pathway.processingTime}</span>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Key Requirements:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {pathway.requirements.map((req, index) => (
                      <li key={index}>
                        <span className="font-medium">{req.label}:</span> {req.value}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <Button
                className="mt-6 w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(pathway);
                }}
              >
                Select Pathway
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default VisaPathwaySelection;